/** English dictionary. Mirrors the Italian one in /it.js. */
export const en = {
  common: {
    loading: "Loading…",
    saving: "Saving…",
    cancel: "Cancel",
    confirm: "Confirm",
    close: "Close",
    back: "Back",
    continue: "Continue",
    retry: "Retry",
    refresh: "Refresh",
    yes: "Yes",
    no: "No",
    optional: "optional",
    required: "required",
    processing: "Processing…",
    saveFolder: "Save to folder",
    downloadZip: "Download ZIP",
    archiveDownloaded: "Archive downloaded",
    saveCanceled: "Save canceled",
    page: "Page",
    delete: "Delete",
  },
  nav: {
    dashboard: "Dashboard",
    acroform: "AcroForm",
    csvBatch: "CSV batch",
    editor: "Editor",
    subscribe: "Subscription",
    admin: "Super admin",
    logout: "Sign out",
    account: "Account",
    tabs: {
      batch: "Identical batch",
      csv: "From CSV (mail-merge)",
      form: "Form fields (AcroForm)",
      admin: "Admin",
    },
  },
  language: {
    label: "Language",
    italian: "Italiano",
    english: "English",
  },
  auth: {
    stepLogin: "00 · Sign in",
    stepRegister: "00 · Sign up",
    stepForgot: "00 · Password recovery",
    stepReset: "00 · New password",
  },
  login: {
    title: "Sign in to your account",
    subtitle: "Welcome back. Manage templates, subscription and account.",
    emailLabel: "Email",
    passwordLabel: "Password",
    forgot: "Forgot password?",
    submit: "Sign in",
    submitting: "Signing in…",
    noAccount: "Don't have an account?",
    toRegister: "Sign up →",
    errorInvalid: "Invalid credentials",
  },
  register: {
    title: "Create your account",
    subtitle: "7-day free trial. No credit card required.",
    nameLabel: "Name",
    namePlaceholder: "Jane Doe",
    emailLabel: "Email",
    emailPlaceholder: "name@example.com",
    passwordLabel: "Password",
    passwordPlaceholder: "min 6 characters",
    submit: "Start free trial",
    submitting: "Creating account…",
    footerTagline: "7-day free trial. No credit card required. No automatic charges.",
    footerDetail: "No payment now. Decide whether to subscribe at the end of your trial.",
    haveAccount: "Already have an account?",
    toLogin: "Sign in →",
    successTrial: "Account created. You have a 7-day free trial.",
    successNoTrial: "Account created. You've already used the free trial with this email.",
  },
  forgot: {
    title: "Recover your password",
    subtitle: "Enter the email associated with your account.",
    emailLabel: "Email",
    submit: "Send recovery link",
    submitting: "Sending…",
    done: "If the email is registered, you'll receive instructions shortly.",
    backToLogin: "Back to sign in",
  },
  reset: {
    title: "Set a new password",
    subtitle: "Choose a password with at least 6 characters.",
    passwordLabel: "New password",
    confirmLabel: "Confirm password",
    submit: "Update password",
    submitting: "Updating…",
    mismatch: "Passwords do not match.",
    tooShort: "Password must be at least 6 characters.",
    done: "Password updated. You can now sign in.",
  },
  subscribe: {
    pageTitle: "Subscription",
    trialBadge: "Free trial · through {date}",
    trialBody: "You can use all REVO PDF Loader features through {date}. The trial is free, no credit card, no automatic charges. When it ends, subscribe to keep using the PDF features.",
    stripeTrialBody: "You can use all REVO PDF Loader features through {date}. The monthly subscription will start right after.",
    activeTitle: "Subscription active",
    activeBody: "Your subscription is active. Thank you!",
    canceledTitle: "Subscription canceled",
    canceledBody: "Your subscription has been canceled. You can use the app through {date}.",
    pastDueTitle: "Payment pending",
    pastDueBody: "The payment did not go through. Please update your payment method to keep access.",
    pricingTitle: "Free trial over",
    pricingTitleExpiredTrial: "Your 7-day free trial has ended.",
    pricingSub: "Subscribe for €9.90/month to resume right away. First charge today, cancel anytime.",
    priceLabel: "€9.90",
    pricePeriod: "/month",
    noAutoCharge: "No credit card. No automatic charges.",
    bullets: {
      firstToday: "First charge today, then €9.90/month",
      cancelAnytime: "Cancel in one click, access until period ends",
      secureStripe: "Secure payment via Stripe",
    },
    ctaSubscribe: "Subscribe for €9.90 / month",
    cancelAnytime: "You can cancel anytime",
    firstChargeNote: "First charge today, then €9.90 per month. Cancel anytime.",
    cancelBtn: "Cancel subscription",
    cancelConfirmTitle: "Confirm cancellation?",
    cancelConfirmBody: "You'll be able to use the app until the end of the current period.",
    toast: {
      redirecting: "Redirecting to Stripe…",
      canceled: "Subscription canceled.",
      genericError: "Something went wrong. Please try again in a moment.",
    },
    adminDisabled: "Your account has been disabled. Please contact support.",
    reasonTrialUsed: "You've already used the free trial with this email.",
    reasonTrialExpired: "Your free trial has ended.",
  },
  subscribeSuccess: {
    title: "Thank you!",
    bodyActive: "Your subscription is now active.",
    bodyTrial: "Your free trial has been activated.",
    goDashboard: "Go to dashboard",
    verifying: "Verifying your payment…",
  },
  subscribeCancel: {
    title: "Payment canceled",
    body: "No charge was made. You can try again anytime.",
    retry: "Try again",
    toSubscribe: "Back to subscription",
  },
  shell: {
    statusTrial: "Trial · through {date}",
    statusFreeTrial: "Free trial · through {date}",
    statusCancelAt: "Cancels on {date}",
    statusActive: "Subscribed",
    statusPastDue: "Payment pending",
    statusGenericActive: "Active",
  },
  editor: {
    subtitleEmpty: "no template loaded",
    step1: "01 · Template",
    step1Title: "Upload the first PDF",
    dropHereCta: "Drag or click",
    dropHereHint: "A reference PDF",
    processing: "Processing...",
    step2: "02 · Input folder",
    step2Title: "PDFs to fill",
    dropFolderCta: "Drop the folder",
    dropFolderHint: "or select multiple PDFs",
    filesSelected: "{n} files selected",
    step3: "03 · Processing",
    step3Title: "Output folder",
    outputFolderHint: "Pick a folder on your PC; the subfolder {name}-output will be created and PDFs will be overwritten.",
    outputFolderHintZip: "Your browser doesn't support direct saving. A ZIP file will be downloaded.",
    emptyCanvasTitle: "Upload a reference PDF to get started",
    emptyCanvasBody: "Click on the PDF to place fields. Each field will be replicated identically on every PDF in the input folder.",
    pageIndicator: "Page {current} / {total}",
    canvasMeta: "{w} × {h} pt · preview {pw}×{ph}px",
    rightTitle: "Fields list",
    rightSub: "Positioning",
    noFieldsHint: "Click on the PDF to add a field",
    emptyField: "(empty)",
    editField: "Edit field",
    textLabel: "Text",
    fontSizeLabel: "Font size (preview px)",
    deleteField: "Delete field",
    defaultText: "Text",
    toasts: {
      templateLoaded: "Template loaded: {n} page(s)",
      archiveDownloaded: "Archive downloaded ({n} PDFs)",
      savedInFolder: "{n} PDFs saved to {folder}/",
      needPdfs: "Add at least one PDF to the input folder",
      needFields: "Add at least one field on the template",
    },
  },
  csvBatch: {
    subtitleEmpty: "upload a PDF template",
    step1: "01 · PDF template",
    step1Title: "Upload reference",
    dropCta: "Drag or click",
    step2: "02 · CSV data",
    step2Title: "CSV file",
    selectCsv: "Select CSV",
    oneRowOnePdf: "1 row = 1 PDF generated",
    columnsLabel: "Columns (click to assign to the selected field)",
    columnUsedTip: "Column already used by a field",
    columnFreeTip: "Column available",
    columnsSummary: "{used}/{total} columns mapped · {rows}+ rows",
    step3: "03 · Process",
    processHint: "Each CSV row → one PDF. Placeholders {ph} are replaced.",
    subfolder: "Subfolder: {name}.",
    emptyCanvasTitle: "Upload the PDF template",
    emptyCanvasBody: "Place fields as {ph} placeholders. Each CSV row will generate a distinct PDF.",
    pageIndicator: "Page {current} / {total}",
    rightTitle: "Fields list",
    rightSub: "Positioning",
    noFieldsHint: "Click on the PDF to add a field",
    emptyField: "(empty)",
    editField: "Edit field",
    textWithCol: "Text (use {colph})",
    fontSizeLabel: "Font size (px)",
    deleteField: "Delete field",
    defaultText: "Text",
    toasts: {
      templateLoaded: "Template loaded: {n} page(s)",
      csvLoaded: "CSV loaded: {n} columns",
      needTemplate: "Upload the PDF template",
      needCsv: "Upload the CSV",
      needField: "Add at least one field",
      selectFieldFirst: "First select a field (click on the PDF or on a field in the list)",
      savedInFolder: "{n} PDFs saved to {folder}/",
    },
  },
  acroform: {
    subtitleEmpty: "upload a PDF with form fields",
    step1: "01 · Reference PDF",
    step1Title: "Field detection",
    analyzing: "Analyzing…",
    uploadFillable: "Upload fillable PDF",
    noForm: "This PDF has no AcroForm fields. Switch to Identical batch mode.",
    step2: "02 · Input folder",
    step2Title: "PDFs to fill",
    dropCta: "Drag or select",
    sameNameFill: "Fields with the same name will be filled",
    filesCount: "{n} files",
    step3: "03 · Processing",
    subfolderHint: "Subfolder: {name}. Existing files will be overwritten.",
    emptyTitle: "Fill the PDF form fields",
    emptyBody: "Upload a PDF with AcroForm fields (fillable forms) to automatically detect field names. Then apply the same values to all PDFs with identical structure.",
    fieldsDetected: "{n} fields detected",
    valuesTitle: "Values to apply",
    fieldMeta: "{type} · pg {page}",
    toasts: {
      detected: "{n} form fields detected",
      noForm: "No AcroForm fields detected in this PDF",
      needPdfs: "Add at least one PDF to the input folder",
      needValue: "Fill at least one value",
      savedInFolder: "{n} PDFs saved to {folder}/",
      archiveDownloaded: "Archive downloaded ({n} PDFs)",
    },
  },
  templates: {
    title: "Saved templates",
    namePlaceholder: "Template name",
    loading: "loading…",
    empty: "No templates saved",
    fieldsCount: "{n} fields",
    confirmDelete: "Delete the template?",
    toasts: {
      nameRequired: "Enter a name",
      cannotSave: "Load a template and add at least one field",
      saved: "Template saved",
      deleted: "Template deleted",
      loaded: "Loaded: {name}",
    },
  },
  admin: {
    title: "Super admin · users",
    refresh: "refresh",
    auditLog: "Audit log",
    wipeAntifraud: "Reset antifraud",
    cols: {
      email: "Email",
      name: "Name",
      role: "Role",
      status: "Status",
      payment: "Payment",
      trial: "Trial",
      createdAt: "Created",
      actions: "Actions",
    },
    active: "active",
    inactive: "disabled",
    delete: "Delete",
    confirmDeleteTitle: "Delete this account?",
    confirmDeleteBody: "The account will be removed. This action cannot be undone.",
    wipe: {
      dialogTitle: "Reset antifraud for an email",
      dialogDesc: "This operation deletes the antifraud history record (email_trial_state) for the single email provided.",
      newTrial: "On next registration the user will receive a new 7-day free trial.",
      warning: "Warning: the application account must have been already deleted from the users list. Otherwise the operation is rejected (409).",
      emailLabel: "Target email",
      phraseLabel: "Confirmation: type {phrase}",
      confirmBtn: "Reset antifraud",
      busy: "Resetting…",
      successDeleted: "Antifraud reset for {email}. The user can receive a new 7-day trial.",
      successNoop: "No antifraud record found for {email}.",
    },
    audit: {
      title: "Super-admin audit log",
      subtitle: "Last {n} operations loaded.",
      empty: "No operations recorded.",
      loading: "loading…",
      actionDeleteUser: "delete user",
      actionWipeAntifraud: "reset antifraud",
    },
  },
  errors: {
    network: "Connection problem. Please try again.",
    unknown: "Something went wrong. Please try again.",
    serverBusy: "Service unavailable right now. Please try again shortly.",
    invalidCredentials: "Invalid credentials.",
    emailExists: "An account with this email already exists. Sign in or recover your password.",
    emailInvalid: "Invalid email.",
    passwordWeak: "Password too short (minimum 6 characters).",
    trialNotAvailable: "You've already used the free trial with this email.",
    requireAuth: "Please sign in to continue.",
    accessBlocked: "Your access is limited. Subscribe to keep using the app.",
    tooManyAttempts: "Too many attempts. Please wait before retrying.",
  },
};
